import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import SearchInput from '../components/SearchInput';
import { usePagination } from '../hooks/usePagination';
import { useDebounce } from '../hooks/useDebounce';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { 
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '../components/ui/pagination';
import { Plus, Trash2, Edit2, Briefcase } from 'lucide-react';
import { toast } from 'sonner';

const ServicesPage = () => {
  const { isAdmin } = useAuth();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    price: '',
    recurrence: '1',
    generateInvoice: false,
    status: 'ACTIVE',
  });

  // Pagination state
  const [paginationData, setPaginationData] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  });

  const pagination = usePagination({
    initialPage: 1,
    itemsPerPage: 10,
    totalItems: paginationData.totalItems,
  });

  useEffect(() => {
    loadServices();
  }, [pagination.currentPage, debouncedSearchTerm]);

  const loadServices = async () => {
    try {
      setLoading(true);
      const response = await api.getServices({
        page: pagination.currentPage,
        limit: pagination.itemsPerPage,
        search: debouncedSearchTerm,
      });
      
      // Handle both paginated and non-paginated responses for backward compatibility
      if (response.data && response.pagination) {
        setServices(response.data);
        setPaginationData(response.pagination);
      } else {
        // Fallback for non-paginated response
        setServices(Array.isArray(response) ? response : []);
        setPaginationData({
          currentPage: 1,
          totalPages: 1,
          totalItems: Array.isArray(response) ? response.length : 0,
          itemsPerPage: 10,
        });
      }
    } catch (error) {
      toast.error('Erro ao carregar serviços');
      setServices([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (service = null) => {
    if (service) {
      setEditingService(service);
      setFormData({
        code: service.code || '',
        name: service.name,
        description: service.description || '',
        price: service.price.toString(),
        recurrence: service.recurrence?.toString() || '1',
        generateInvoice: service.generateInvoice || false,
        status: service.status,
      });
    } else {
      setEditingService(null);
      setFormData({
        code: '',
        name: '',
        description: '',
        price: '',
        recurrence: '1',
        generateInvoice: false,
        status: 'ACTIVE',
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        price: parseFloat(formData.price),
        recurrence: parseInt(formData.recurrence),
        generateInvoice: formData.generateInvoice,
      };

      if (editingService) {
        await api.updateService(editingService.id, data);
        toast.success('Serviço atualizado com sucesso');
      } else {
        await api.createService(data);
        toast.success('Serviço criado com sucesso');
      }

      setDialogOpen(false);
      setEditingService(null);
      setFormData({
        code: '',
        name: '',
        description: '',
        price: '',
        recurrence: '1',
        generateInvoice: false,
        status: 'ACTIVE',
      });
      loadServices();
    } catch (error) {
      toast.error(error.message || 'Erro ao salvar serviço');
    }
  };

  const handleToggleEnabled = async (service) => {
    try {
      await api.toggleService(service.id);
      toast.success(`Serviço ${!service.enabled ? 'ativado' : 'desativado'} com sucesso`);
      loadServices();
    } catch (error) {
      toast.error('Erro ao alterar status do serviço');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja deletar este serviço?')) return;
    try {
      await api.deleteService(id);
      toast.success('Serviço deletado');
      loadServices();
    } catch (error) {
      toast.error('Erro ao deletar serviço');
    }
  };

  const handlePageChange = (page) => {
    pagination.goToPage(page);
  };

  const handleSearchChange = (value) => {
    setSearchTerm(value);
  };

  const getStatusBadge = (status) => {
    const variants = {
      ACTIVE: { variant: 'default', label: 'Ativo' },
      INACTIVE: { variant: 'secondary', label: 'Inativo' },
      COMPLETED: { variant: 'outline', label: 'Concluído' },
    };
    const config = variants[status] || variants.ACTIVE;
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="services-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Serviços</h1>
          <p className="text-muted-foreground mt-1">Gerencie o catálogo de serviços</p>
        </div>
        {isAdmin() && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => handleOpenDialog()} data-testid="add-service-button">
                <Plus className="w-4 h-4 mr-2" />
                Novo Serviço
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editingService ? 'Editar Serviço' : 'Criar Novo Serviço'}</DialogTitle>
                <DialogDescription>
                  {editingService ? 'Atualize os dados do serviço' : 'Adicione um novo serviço ao catálogo'}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="code">Código</Label>
                  <Input
                    id="code"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="name">Nome do Serviço</Label>
                  <Input
                    id="name"
                    placeholder="Ex: Consultoria, Manutenção..."
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value.toUpperCase() })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Descrição</Label>
                  <Textarea
                    id="description"
                    placeholder="Descreva o serviço..."
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="price">Preço (R$)</Label>
                  <Input
                    id="price"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="recurrence">Recorrência (Qtde de Vezes - Pacote)</Label>
                  <Input
                    id="recurrence"
                    type="number"
                    min="1"
                    value={formData.recurrence}
                    onChange={(e) => setFormData({ ...formData, recurrence: e.target.value })}
                    required
                  />
                </div>
                <div className="flex items-center justify-between space-x-2">
                  <Label htmlFor="generateInvoice" className="flex-1">
                    Gera Fatura
                  </Label>
                  <Switch
                    id="generateInvoice"
                    checked={formData.generateInvoice}
                    onCheckedChange={(checked) => setFormData({ ...formData, generateInvoice: checked })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="status">Status</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(value) => setFormData({ ...formData, status: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ACTIVE">Ativo</SelectItem>
                      <SelectItem value="INACTIVE">Inativo</SelectItem>
                      <SelectItem value="COMPLETED">Concluído</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button type="submit" className="w-full">
                  {editingService ? 'Atualizar Serviço' : 'Criar Serviço'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Search Input */}
      <SearchInput
        value={searchTerm}
        onChange={handleSearchChange}
        placeholder="Pesquisar por código, nome ou descrição..."
        searchFields={['Código', 'Nome', 'Descrição']}
      />

      {/* Services Table */}
      <Card>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="px-6 py-3 text-left text-sm font-semibold">Código</th>
                <th className="px-6 py-3 text-left text-sm font-semibold">Nome</th>
                <th className="px-6 py-3 text-left text-sm font-semibold">Descrição</th>
                <th className="px-6 py-3 text-right text-sm font-semibold">Preço</th>
                <th className="px-6 py-3 text-center text-sm font-semibold">Recorrência</th>
                <th className="px-6 py-3 text-center text-sm font-semibold">Status</th>
                <th className="px-6 py-3 text-center text-sm font-semibold">Ativo</th>
                <th className="px-6 py-3 text-right text-sm font-semibold">Ações</th>
              </tr>
            </thead>
            <tbody>
              {services.map((service) => (
                <tr
                  key={service.id}
                  className="border-b hover:bg-muted/50 transition-colors"
                  data-testid={`service-row-${service.id}`}
                >
                  <td className="px-6 py-4 text-sm font-mono">{service.code}</td>
                  <td className="px-6 py-4 text-sm font-medium">{service.name}</td>
                  <td className="px-6 py-4 text-sm text-muted-foreground line-clamp-1">
                    {service.description || '-'}
                  </td>
                  <td className="px-6 py-4 text-sm font-semibold text-right">
                    R$ {service.price.toFixed(2)}
                  </td>
                  <td className="px-6 py-4 text-sm text-center">
                    {service.recurrence}x
                  </td>
                  <td className="px-6 py-4 text-sm text-center">
                    {getStatusBadge(service.status)}
                  </td>
                  <td className="px-6 py-4 text-sm text-center">
                    {isAdmin() ? (
                      <Switch
                        checked={service.enabled}
                        onCheckedChange={() => handleToggleEnabled(service)}
                        aria-label={`${service.enabled ? 'Desativar' : 'Ativar'} serviço`}
                      />
                    ) : (
                      <Badge variant={service.enabled ? 'default' : 'secondary'}>
                        {service.enabled ? 'Ativo' : 'Inativo'}
                      </Badge>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-right space-x-2">
                    {isAdmin() && (
                      <>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenDialog(service)}
                          data-testid={`edit-service-${service.id}`}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(service.id)}
                          data-testid={`delete-service-${service.id}`}
                        >
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Pagination */}
      {paginationData.totalPages > 1 && (
        <Pagination className="mt-4">
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious 
                onClick={() => handlePageChange(paginationData.currentPage - 1)}
                className={paginationData.currentPage === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
              />
            </PaginationItem>
            
            {Array.from({ length: paginationData.totalPages }, (_, i) => i + 1).map((page) => (
              <PaginationItem key={page}>
                <PaginationLink
                  onClick={() => handlePageChange(page)}
                  isActive={page === paginationData.currentPage}
                  className="cursor-pointer"
                >
                  {page}
                </PaginationLink>
              </PaginationItem>
            ))}
            
            <PaginationItem>
              <PaginationNext 
                onClick={() => handlePageChange(paginationData.currentPage + 1)}
                className={paginationData.currentPage === paginationData.totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}

      {services.length === 0 && !loading && (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Briefcase className="w-12 h-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground text-center">
              {searchTerm ? 'Nenhum serviço encontrado.' : 'Nenhum serviço encontrado.'}
              {isAdmin() && !searchTerm && ' Crie o primeiro!'}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ServicesPage;
